/**
 * Lot Size Utility
 * 
 * Provides lot size lookup and quantity formatting helpers.
 * Currently uses static exchange-defined lot sizes as fallback.
 * 
 * TODO: Replace with API-fetched lot sizes when broker API is integrated.
 * The getLotSize() function is the single point to swap from static → API data.
 */

// Static lot sizes — these match current NSE/BSE exchange definitions.
// When broker API is integrated, these serve as fallback only.
const LOT_SIZES: Record<string, number> = {
  NIFTY: 65,
  BANKNIFTY: 15,
  FINNIFTY: 25,
  SENSEX: 10,
  BANKEX: 15,
  MIDCPNIFTY: 50,
}

/**
 * Get lot size for an underlying.
 * Returns the exchange-defined lot size, or 25 as default.
 * 
 * TODO: When API is integrated, this should first check a cached API response,
 * falling back to the static map above.
 */
export function getLotSize(underlying: string): number {
  return LOT_SIZES[underlying?.toUpperCase()] || 25
}

/**
 * Calculate total quantity from lots and underlying.
 */
export function getQty(lots: number, underlying: string): number {
  return lots * getLotSize(underlying)
}

/**
 * Format lots + quantity for display.
 * Example: formatLotsQty(2, 'NIFTY') → "2 lots (50 qty)"
 */
export function formatLotsQty(lots: number, underlying: string): string {
  const qty = getQty(lots, underlying)
  return `${lots} lot${lots !== 1 ? 's' : ''} (${qty} qty)`
}

/**
 * Compact format for badges and tight spaces.
 * Example: formatLotsQtyCompact(2, 'NIFTY') → "Lot: 2, Qty: 50"
 */
export function formatLotsQtyCompact(lots: number, underlying: string): string {
  const qty = getQty(lots, underlying)
  return `Lot: ${lots}, Qty: ${qty}`
}
